import type { RefObject } from 'react'
import { formatCurrency } from '@/utils/format-currency'
import type { ComboVariationWithPricing } from '@/types'
import { SLIDE_GREEN_DARK } from './slidePreviewTheme'

type SlidePreviewCoverProps = {
  previewRef: RefObject<HTMLDivElement>
  empresaNome?: string
  variations: ComboVariationWithPricing[]
}

export function SlidePreviewCover({ previewRef, empresaNome, variations }: SlidePreviewCoverProps) {
  return (
    <div className="w-full flex flex-col items-center justify-center p-1 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-lg overflow-y-auto shrink-0 select-none">
      <div
        ref={previewRef}
        data-proposal-slide-preview=""
        className="relative w-full rounded-lg overflow-hidden shadow-2xl flex flex-col justify-between p-6"
        style={{
          aspectRatio: '16/9',
          containerType: 'inline-size',
          backgroundColor: SLIDE_GREEN_DARK,
          backgroundImage:
            'radial-gradient(circle at 80% 20%, rgba(255,47,134,0.22) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(124,58,237,0.18) 0%, transparent 42%)',
        }}
      >
        <div className="flex justify-between items-start">
          <div>
            <span className="text-[#ff2f86] font-bold text-[clamp(8px,1.8cqw,16px)] uppercase tracking-wider block">
              Capa Comparativa
            </span>
            <h2 className="text-white font-bold text-[clamp(12px,3.2cqw,28px)] leading-tight">
              {empresaNome || 'Seu Negócio'}
            </h2>
          </div>
          <div className="text-right">
            <span className="text-zinc-400 text-[clamp(6px,1.2cqw,11px)]">Opções de Investimento</span>
          </div>
        </div>

        <div className="flex gap-4 items-stretch justify-center my-auto w-full">
          {variations.map((v, index) => {
            const isRecommended = variations.length === 3 ? index === 1 : index === 0
            return (
              <div
                key={v.id}
                className={`flex-1 rounded-lg p-4 border flex flex-col justify-between transition-all relative ${
                  isRecommended
                    ? 'bg-white/10 border-[#ff2f86] shadow-[0_0_15px_rgba(255,47,134,0.24)]'
                    : 'bg-white/5 border-zinc-800'
                }`}
                style={{ minHeight: '55%' }}
              >
                {isRecommended && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#ff2f86] text-white px-2 py-0.5 rounded-full text-[clamp(4px,1cqw,9px)] font-bold uppercase tracking-wider">
                    Recomendado
                  </div>
                )}
                <div>
                  <h3 className="text-white font-bold text-[clamp(10px,2cqw,18px)] mb-0.5">{v.nome}</h3>
                  <p className="text-zinc-400 text-[clamp(5px,1.2cqw,11px)] line-clamp-2 mb-2 leading-snug">
                    {v.descricao || 'Personalização sob medida.'}
                  </p>
                  <div className="space-y-1 my-2 max-h-[8cqh] overflow-hidden">
                    {v.items?.map((item) => (
                      <div key={item.id} className="flex items-center gap-1.5">
                        <span className="text-[#ff2f86] text-[clamp(5px,1.1cqw,10px)]">✓</span>
                        <span className="text-zinc-300 text-[clamp(4px,1.1cqw,10px)] truncate">
                          {item.descricao || item.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2 mt-2">
                  <span className="text-zinc-400 text-[clamp(5px,1cqw,9px)] block uppercase tracking-wider">
                    Investimento
                  </span>
                  <span className="text-[#ff2f86] font-extrabold text-[clamp(11px,2.4cqw,20px)] block">
                    {formatCurrency(v.valorFinal)}
                    <span className="text-[clamp(6px,1.1cqw,10px)] font-normal text-zinc-400">/mês</span>
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex justify-between items-center text-zinc-500 text-[clamp(5px,1cqw,9px)] border-t border-white/5 pt-2 w-full">
          <span>Agrada Digital · CRM</span>
          <span>16:9 Widescreen Presentation</span>
        </div>
      </div>
    </div>
  )
}
